import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useStore } from "store/zustand";
import { useGlobalLoader } from "contexts/GlobalLoaderContext";
import { GoogleAuthProvider, OAuthProvider, getAuth } from "firebase/auth";
import { signInWithProvider } from "utils/authFunctions";
import { handleSendCode } from "./SendCodes";
import Button from "../../components/Button";
import "../../assets/css/style.css";
import "../../assets/icons/style.css";
import { ArrowLeft } from "lucide-react";

const Signup: React.FC = () => {
  const { referralCode } = useParams<{ referralCode: string }>();
  const { setIsAuthenticated } = useStore();
  const { showLoader, hideLoader } = useGlobalLoader();
  const [username, setUsername] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [isVerified, setIsVerified] = useState<boolean | null>(null);
  const [isEmailVerifying, setIsEmailVerifying] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false); // Local loading state
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: boolean }>(
    {}
  );
  const googleProvider = new GoogleAuthProvider();
  const appleProvider = new OAuthProvider("apple.com");
  const navigate = useNavigate();

  const auth = getAuth();

  // Helper function to remove specific error messages from error string
  const removeErrorMessages = (currentError: string, messagesToRemove: string[]): string => {
    if (!currentError) return "";
    const currentErrors = currentError.split('\n');
    const filteredErrors = currentErrors.filter(err => 
      !messagesToRemove.some(msg => err.includes(msg))
    );
    return filteredErrors.join('\n');
  };

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Strictly prevent input beyond 20 characters
    if (value.length > 20) {
      setFieldErrors({ ...fieldErrors, username: true });
      const currentErrors = error ? error.split('\n') : [];
      const newErrors = currentErrors.filter(err => !err.includes("Username must be 20 characters or less"));
      newErrors.push("Username must be 20 characters or less");
      setError(newErrors.join('\n'));
      // Don't update state if it exceeds 20
      return;
    }
    // Clear errors if username is valid
    const errorsToRemove: string[] = [];
    if (value.length > 0) {
      errorsToRemove.push("Username is required");
    }
    if (value.length <= 20) {
      errorsToRemove.push("Username must be 20 characters or less");
    }
    
    if (errorsToRemove.length > 0) {
      const newError = removeErrorMessages(error, errorsToRemove);
      setError(newError);
      if (value.length > 0 && value.length <= 20) {
        setFieldErrors({ ...fieldErrors, username: false });
      }
    }
    setUsername(value);
  };

  const handleUsernameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    // Prevent typing if already at 20 characters (allow deletion and navigation keys)
    if (target.value.length >= 20 && 
        e.key !== 'Backspace' && 
        e.key !== 'Delete' && 
        e.key !== 'ArrowLeft' && 
        e.key !== 'ArrowRight' &&
        e.key !== 'Tab' &&
        e.key !== 'Home' &&
        e.key !== 'End' &&
        !e.ctrlKey && 
        !e.metaKey &&
        !e.altKey) {
      e.preventDefault();
      setFieldErrors({ ...fieldErrors, username: true });
      const currentErrors = error ? error.split('\n') : [];
      const newErrors = currentErrors.filter(err => !err.includes("Username must be 20 characters or less"));
      newErrors.push("Username must be 20 characters or less");
      setError(newErrors.join('\n'));
    }
  };

  const handleEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    
    // Clear email-related errors when email becomes valid
    const errorsToRemove: string[] = [];
    if (value.length > 0) {
      errorsToRemove.push("Email is required");
    }
    if (value.length > 0 && isEmail(value)) {
      errorsToRemove.push("Please enter a valid email");
      setFieldErrors({ ...fieldErrors, email: false });
    }
    
    if (errorsToRemove.length > 0) {
      const newError = removeErrorMessages(error, errorsToRemove);
      setError(newError);
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    let newPassword = value;
    
    if (value.length > 20) {
      newPassword = value.substring(0, 20);
      const currentErrors = error ? error.split('\n') : [];
      const newErrors = currentErrors.filter(err => !err.includes("Password must be 20 characters or less"));
      newErrors.push("Password must be 20 characters or less");
      setError(newErrors.join('\n'));
      setPassword(newPassword);
      return;
    }
    
    setPassword(newPassword);
    
    // Clear password-related errors when password becomes valid
    const errorsToRemove: string[] = [];
    if (newPassword.length > 0) {
      errorsToRemove.push("Password is required");
    }
    if (newPassword.length <= 20) {
      errorsToRemove.push("Password must be 20 characters or less");
    }
    if (newPassword.length >= 8) {
      errorsToRemove.push("Password must be at least 8 characters long");
    }
    if (newPassword.length > 0 && /[A-Z]/.test(newPassword)) {
      errorsToRemove.push("Password must contain at least one uppercase letter");
    }
    if (newPassword.length > 0 && /[a-z]/.test(newPassword)) {
      errorsToRemove.push("Password must contain at least one lowercase letter");
    }
    if (newPassword.length > 0 && /\d/.test(newPassword)) {
      errorsToRemove.push("Password must contain at least one number");
    }
    if (newPassword.length > 0 && /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(newPassword)) {
      errorsToRemove.push("Password must contain at least one special character");
    }
    
    // Check if password matches confirm password
    if (newPassword.length > 0 && confirmPassword && newPassword === confirmPassword) {
      errorsToRemove.push("Passwords do not match");
      setFieldErrors({ ...fieldErrors, confirmPassword: false });
    }
    
    if (errorsToRemove.length > 0) {
      const newError = removeErrorMessages(error, errorsToRemove);
      setError(newError);
      
      // Update fieldErrors if password is valid
      if (newPassword.length >= 8 && 
          /[A-Z]/.test(newPassword) && 
          /[a-z]/.test(newPassword) && 
          /\d/.test(newPassword) && 
          /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(newPassword) &&
          newPassword.length <= 20) {
        setFieldErrors({ ...fieldErrors, password: false });
      }
    }
  };
  const handleConfirmPasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    setConfirmPassword(value);
    
    // Clear confirm password-related errors when passwords match
    const errorsToRemove: string[] = [];
    if (value.length > 0) {
      errorsToRemove.push("Confirm password is required");
    }
    if (value.length > 0 && password && value === password) {
      errorsToRemove.push("Passwords do not match");
      setFieldErrors({ ...fieldErrors, confirmPassword: false });
    }
    
    if (errorsToRemove.length > 0) {
      const newError = removeErrorMessages(error, errorsToRemove);
      setError(newError);
    }
  };

  const isEmail = (input: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input);
  const isStrongPassword = (password: string) => {
    return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{8,}$/.test(
      password
    );
  };

  const validateForm = () => {
    const errors: string[] = [];
    const fieldErrors: { [key: string]: boolean } = {};

    if (!username) {
      errors.push("Username is required");
      fieldErrors.username = true;
    }
    if (username && username.length > 20) {
      errors.push("Username must be 20 characters or less");
      fieldErrors.username = true;
    }
    if (!email) {
      errors.push("Email is required");
      fieldErrors.email = true;
    }
    if (!password) {
      errors.push("Password is required");
      fieldErrors.password = true;
    }
    if (!confirmPassword) {
      errors.push("Confirm password is required");
      fieldErrors.confirmPassword = true;
    }
    if (email && !isEmail(email)) errors.push("Please enter a valid email");
    if (password && password.length < 8)
      errors.push("Password must be at least 8 characters long");
    if (password && !/[A-Z]/.test(password))
      errors.push("Password must contain at least one uppercase letter");
    if (password && !/[a-z]/.test(password))
      errors.push("Password must contain at least one lowercase letter");
    if (password && !/\d/.test(password))
      errors.push("Password must contain at least one number");
    if (password && !/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password))
      errors.push("Password must contain at least one special character");
    if (password && confirmPassword && password !== confirmPassword)
      errors.push("Passwords do not match");

    setFieldErrors(fieldErrors);
    setError(errors.join("\n"));
    return errors.length === 0;
  };

  const handleSendCodeWrapper = async () => {
    if (!validateForm()) return;

    try {
      await handleSendCode(
        email,
        username,
        password,
        setError,
        setIsEmailVerifying,
        (path: string) =>
          navigate(
            referralCode
              ? `${path}?referral=${encodeURIComponent(referralCode)}`
              : path
          ), // Append referral code to navigation
        setIsVerified,
        setIsAuthenticated,
        referralCode
      );
    } catch (error: any) {
      setError(error.message || "Failed to send verification code.");
    }
  };

  const signInWithGoogle = async () => {
    setIsLoading(true);
    showLoader();
    try {
      const userOrError = await signInWithProvider(
        googleProvider,
        setIsAuthenticated,
        navigate,
        true
      ); // Pass isSignup: true
      if (typeof userOrError === "string") {
        setError(userOrError);
      }
    } catch (error: any) {
      setError(error.message || "Google sign-up failed.");
    } finally {
      hideLoader();
      setIsLoading(false);
    }
  };

  const signInWithApple = async () => {
    setIsLoading(true);
    showLoader();
    try {
      const userOrError = await signInWithProvider(
        appleProvider,
        setIsAuthenticated,
        navigate,
        true
      ); // Pass isSignup: true
      if (typeof userOrError === "string") {
        setError(userOrError);
      }
    } catch (error: any) {
      setError(error.message || "Apple sign-up failed.");
    } finally {
      hideLoader();
      setIsLoading(false);
    }
  };

  const handleSubmit = () => {
    if (validateForm()) {
      handleSendCodeWrapper();
    }
  };

  return (
    <div className="lg:col-span-3 col-span-12 lg:col-start-9">
      <div className="register-1">
        <img className="w-40" src="/images/logo.svg" alt="Logo" />
        <div className="flex items-center mt-6 gap-3">
          <ArrowLeft
            onClick={() => navigate("/login")}
            className="w-5 h-5 text-grease-100 cursor-pointer"
            aria-label="Go back to login"
          />
          <p className="text-2xl font-semibold text-grease-100">
            Sign up to continue
          </p>
        </div>

        <div className="flex gap-3 mt-8">
          <div
            onClick={signInWithGoogle}
            className="p-3 border border-lightgray-50 rounded-lg flex gap-3 items-center flex-1 cursor-pointer"
          >
            <img className="w-5" src="/images/_Google.svg" alt="Google" />
            <p className="text-grease-50 text-sm font-medium">Google</p>
          </div>

          <div
            onClick={signInWithApple}
            className="p-3 border border-lightgray-50 rounded-lg flex gap-3 items-center flex-1 cursor-pointer"
          >
            <img className="w-5" src="/images/apple.svg" alt="Apple" />
            <p className="text-grease-50 text-sm font-medium">Apple</p>
          </div>
        </div>

        <div className="flex items-center gap-4 mt-4">
          <div className="w-full h-0.25 bg-lightgray-100"></div>
          <p className="text-grease-100 text-xs">Or</p>
          <div className="w-full h-0.25 bg-lightgray-100"></div>
        </div>

        <p className="mt-5 text-md font-medium">
          Get a register code emailed to you.
        </p>
        <div className="mt-6">
          <input
            type="text"
            name="new-username"
            id="username"
            autoComplete="off"
            value={username}
            onChange={handleUsernameChange}
            onKeyDown={handleUsernameKeyDown}
            onPaste={(e) => {
              e.preventDefault();
              const pastedText = e.clipboardData.getData('text');
              const newValue = (username + pastedText).substring(0, 20);
              const errorsToRemove: string[] = [];
              
              if (newValue.length >= 20 && (username + pastedText).length > 20) {
                setFieldErrors({ ...fieldErrors, username: true });
                const currentErrors = error ? error.split('\n') : [];
                const newErrors = currentErrors.filter(err => !err.includes("Username must be 20 characters or less"));
                newErrors.push("Username must be 20 characters or less");
                setError(newErrors.join('\n'));
              } else {
                if (newValue.length > 0) {
                  errorsToRemove.push("Username is required");
                }
                if (newValue.length <= 20) {
                  errorsToRemove.push("Username must be 20 characters or less");
                }
                if (errorsToRemove.length > 0) {
                  const newError = removeErrorMessages(error, errorsToRemove);
                  setError(newError);
                }
                setFieldErrors({ ...fieldErrors, username: false });
              }
              setUsername(newValue);
            }}
            onInput={(e) => {
              const target = e.target as HTMLInputElement;
              if (target.value.length > 20) {
                target.value = target.value.substring(0, 20);
                setFieldErrors({ ...fieldErrors, username: true });
                const currentErrors = error ? error.split('\n') : [];
                const newErrors = currentErrors.filter(err => !err.includes("Username must be 20 characters or less"));
                newErrors.push("Username must be 20 characters or less");
                setError(newErrors.join('\n'));
                setUsername(target.value);
              } else {
                const errorsToRemove: string[] = [];
                if (target.value.length > 0) {
                  errorsToRemove.push("Username is required");
                }
                if (target.value.length <= 20) {
                  errorsToRemove.push("Username must be 20 characters or less");
                }
                if (errorsToRemove.length > 0) {
                  const newError = removeErrorMessages(error, errorsToRemove);
                  setError(newError);
                  setFieldErrors({ ...fieldErrors, username: false });
                }
              }
            }}
            maxLength={20}
            className={`block w-full p-4 rounded-lg bg-gray-50 text-sm text-grease-100 placeholder-gray-400 
            border border-transparent
            focus:border-light-yellow
            focus:outline-none 
             focus:ring-1 
             focus:ring-light-yellow 
             focus:ring-offset-0
            ${fieldErrors.username ? " border border-red-500" : ""}`}
            placeholder="Name"
          />
          <input
            type="email"
            name="new-emailOrPhone"
            id="emailOrPhone"
            value={email}
            autoComplete="new-email"
            onChange={handleEmail}
            className={`block mt-6 w-full p-4 rounded-lg bg-gray-50 text-sm text-grease-100 placeholder-gray-400
            border border-transparent
            focus:border-light-yellow
            focus:outline-none 
             focus:ring-1 
             focus:ring-light-yellow 
             focus:ring-offset-0
             ${fieldErrors.email ? " border border-red-500" : ""}`}
            placeholder="Email"
          />
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              id="password"
              value={password}
              onChange={handlePasswordChange}
              onKeyDown={(e) => {
                if (password.length >= 20 && 
                    e.key !== 'Backspace' && 
                    e.key !== 'Delete' && 
                    e.key !== 'ArrowLeft' && 
                    e.key !== 'ArrowRight' &&
                    e.key !== 'Tab' &&
                    e.key !== 'Home' &&
                    e.key !== 'End' &&
                    !e.ctrlKey && 
                    !e.metaKey &&
                    !e.altKey) {
                  e.preventDefault();
                  const currentErrors = error ? error.split('\n') : [];
                  const newErrors = currentErrors.filter(err => !err.includes("Password must be 20 characters or less"));
                  newErrors.push("Password must be 20 characters or less");
                  setError(newErrors.join('\n'));
                }
              }}
              minLength={6}
              maxLength={20}
              className={`block mt-6 w-full p-4 rounded-lg bg-gray-50 text-sm text-grease-100 placeholder-gray-400  pr-10
              border border-transparent
            focus:border-light-yellow
            focus:outline-none 
             focus:ring-1 
             focus:ring-light-yellow 
             focus:ring-offset-0
             ${fieldErrors.password ? " border border-red-500" : ""}`}
              placeholder="Password"
              autoComplete="new-password"
              data-lpignore="true"
              aria-describedby={
                error.includes("Password") ? "password-error" : undefined
              }
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-grease-100"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              <svg
                className={`shrink-0 size-3.5 ${showPassword ? "hs-password-active" : ""}`}
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path
                  className={showPassword ? "hidden" : ""}
                  d="M9.88 9.88a3 3 0 1 0 4.24 4.24"
                ></path>
                <path
                  className={showPassword ? "hidden" : ""}
                  d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"
                ></path>
                <path
                  className={showPassword ? "hidden" : ""}
                  d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"
                ></path>
                <line
                  className={showPassword ? "hidden" : ""}
                  x1="2"
                  x2="22"
                  y1="2"
                  y2="22"
                ></line>
                <path
                  className={showPassword ? "" : "hidden"}
                  d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"
                ></path>
                <circle
                  className={showPassword ? "" : "hidden"}
                  cx="12"
                  cy="12"
                  r="3"
                ></circle>
              </svg>
            </button>
          </div>
          <div className="relative">
            <input
              type={showConfirmPassword ? "text" : "password"}
              name="confirmPassword"
              id="confirmPassword"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
              data-lpignore="true"
              className={`block mt-6 w-full p-4 rounded-lg bg-gray-50 text-sm text-grease-100 placeholder-gray-400 
            border border-transparent
            focus:border-light-yellow
            focus:outline-none 
             focus:ring-1 
             focus:ring-light-yellow 
             focus:ring-offset-0
             ${fieldErrors.confirmPassword ? " border border-red-500" : ""}`}
              placeholder="Confirm Password"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-grease-100"
              aria-label={
                showConfirmPassword
                  ? "Hide confirm password"
                  : "Show confirm password"
              }
            >
              <svg
                className={`shrink-0 size-3.5 ${showConfirmPassword ? "hs-password-active" : ""}`}
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path
                  className={showConfirmPassword ? "hidden" : ""}
                  d="M9.88 9.88a3 3 0 1 0 4.24 4.24"
                ></path>
                <path
                  className={showConfirmPassword ? "hidden" : ""}
                  d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"
                ></path>
                <path
                  className={showConfirmPassword ? "hidden" : ""}
                  d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"
                ></path>
                <line
                  className={showConfirmPassword ? "hidden" : ""}
                  x1="2"
                  x2="22"
                  y1="2"
                  y2="22"
                ></line>
                <path
                  className={showConfirmPassword ? "" : "hidden"}
                  d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"
                ></path>
                <circle
                  className={showConfirmPassword ? "" : "hidden"}
                  cx="12"
                  cy="12"
                  r="3"
                ></circle>
              </svg>
            </button>
          </div>
        </div>

        {error &&
          error.split("\n").map((err, idx) => (
            <p key={idx} className="text-red-500 mt-2 text-sm">
              {err}
            </p>
          ))}
        <div id="recaptcha-container"></div>
        <Button
          onClick={handleSubmit}
          disabled={isLoading || isEmailVerifying}
          className="w-full p-4 rounded-lg text-white uppercase text-center shadow-button bg-light-yellow text-sm mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading || isEmailVerifying ? "Verifying..." : "Verify My Mail ID"}
        </Button>
        <div className="flex items-center justify-center text-sm gap-2 mt-6">
          <p className="text-grease-200">Do you have an account?</p>
          <a
            className="text-light-yellow cursor-pointer"
            onClick={() => navigate("/login")}
          >
            Sign In
          </a>
        </div>
      </div>
    </div>
  );
};

export default Signup;
